import type {
  InlineTokenDefinition,
  ParsedInlineToken,
} from 'src/modules/shared/types/editor/inline-tokens.types';
/**
 * Regular expression for parsing inline token markup.
 *
 * Matches patterns like `[[kind:id]]` where:
 * - `kind` is alphanumeric with underscores and hyphens
 * - `id` is any sequence of characters except `]`
 *
 * Used globally to iterate through all markup segments in a string.
 *
 * @example
 * ```ts
 * const match = MARKUP_REGEX.exec('Hello [[status:1]] world');
 * // match[0] = '[[status:1]]'
 * // match[1] = 'status'
 * // match[2] = '1'
 * ```
 */
const MARKUP_REGEX = /\[\[([a-zA-Z0-9_-]+):([^\]]+)\]\]/g;

/**
 * Parses markup in the format `[[kind:id]]` into structured tokens.
 *
 * Iterates through the value string, extracting markup segments and resolving them
 * against the provided definitions. Unmatched markup or unresolvable IDs are treated
 * as plain text tokens.
 *
 * @param value - The input string potentially containing markup segments
 * @param definitions - Array of token definitions used to resolve `kind` and `id` to labels
 * @returns Array of parsed tokens (text or domain-specific with resolved labels)
 *
 * @example
 * ```ts
 * const tokens = parseMarkupToTokens(
 *   'Hello [[status:1]] world',
 *   [{ kind: 'status', resolveLabel: (id) => statusMap[id] }]
 * );
 * // Result: [
 * //   { kind: 'text', value: 'Hello ' },
 * //   { kind: 'status', id: '1', label: 'In Progress' },
 * //   { kind: 'text', value: ' world' }
 * // ]
 * ```
 */
export const parseMarkupToTokens = (
  value: string,
  definitions: InlineTokenDefinition[]
): ParsedInlineToken[] => {
  if (!value) return [];

  const tokens: ParsedInlineToken[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  MARKUP_REGEX.lastIndex = 0;

  while ((match = MARKUP_REGEX.exec(value)) !== null) {
    const matchStart = match.index;
    const matchText = match[0] ?? '';

    if (matchStart > lastIndex) {
      tokens.push({
        kind: 'text',
        value: value.slice(lastIndex, matchStart),
      });
    }

    const kind = match[1] ?? '';
    const id = match[2] ?? '';

    const definition = definitions.find((def) => def.kind === kind);

    if (!definition) {
      tokens.push({ kind: 'text', value: matchText });
      lastIndex = matchStart + matchText.length;
      continue;
    }

    const label = definition.resolveLabel(id);

    if (!label) {
      tokens.push({ kind: 'text', value: matchText });
      lastIndex = matchStart + matchText.length;
      continue;
    }

    tokens.push({
      kind,
      id,
      label,
    });

    lastIndex = matchStart + matchText.length;
  }

  if (lastIndex < value.length) {
    tokens.push({
      kind: 'text',
      value: value.slice(lastIndex),
    });
  }

  return tokens;
};

/**
 * Serializes a DOM tree back into markup format.
 *
 * Walks the DOM, collecting text content and converting elements with
 * `data-token-kind` and `data-token-id` attributes into markup segments
 * of the form `[[kind:id]]`. Handles line breaks as newlines.
 *
 * @param root - The root HTML element to serialize
 * @returns Markup string representation of the DOM tree
 *
 * @example
 * ```ts
 * const div = document.createElement('div');
 * const chip = document.createElement('span');
 * chip.dataset.tokenKind = 'status';
 * chip.dataset.tokenId = '1';
 * chip.textContent = 'In Progress';
 * div.appendChild(chip);
 *
 * const markup = serializeDomToMarkup(div);
 * // Result: "[[status:1]]"
 * ```
 */
export const serializeDomToMarkup = (root: HTMLElement): string => {
  const parts: string[] = [];

  const walk = (node: Node): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      parts.push((node as Text).data);
      return;
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;

      const tokenKind = el.dataset.tokenKind;
      const tokenId = el.dataset.tokenId;

      if (tokenKind && tokenId) {
        parts.push(`[[${tokenKind}:${tokenId}]]`);
        return;
      }

      if (el.tagName === 'BR') {
        parts.push('\n');
        return;
      }

      const children = Array.from(el.childNodes);
      children.forEach(walk);
    }
  };

  const children = Array.from(root.childNodes);
  children.forEach(walk);

  return parts.join('');
};
